import { documentService } from '@/services/documents';
import { openBase64PdfInWindow, openDocumentWithAuth } from '@/utils/pdfHelpers';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'generateChangeDocument' });

/**
 * Format a numeric value as US currency (e.g. "$1,234.56").
 * Returns empty string for falsy/zero values when `allowZero` is false (default).
 */
export const formatCurrency = (value: number | null | undefined, allowZero = false): string => {
  if (value == null || (!allowZero && value === 0)) return '';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export type ChangeDocumentType =
  | 'ChangeDocumentIssue'
  | 'ChangeDocumentTurnIn'
  | 'ChangeDocumentExchange';

export interface ChangeDocumentLineItem {
  lin: string;
  nsn: string;
  nomenclature: string;
  thr_phr: 'PHR' | 'THR';
  /** Used by Issue template */
  qty_issued?: string;
  /** Used by Turn-In template */
  qty_turn_in?: string;
  /** Used by Exchange template */
  qty_action?: string;
  /** Used by Exchange template */
  issue_turn_in?: 'Issue' | 'Turn In';
  /** On-hand quantity (all templates) */
  qty_oh?: string;
  /** Unit cost (all templates) */
  unit_cost?: string;
  /** Total cost per line (all templates) */
  total_cost?: string;
  /** Recoverable / Non-Recoverable (all templates) */
  r_nr?: string;
  /** Due date — Issue template only */
  due_date?: string;
}

export interface GenerateChangeDocumentParams {
  documentType: ChangeDocumentType;
  items: ChangeDocumentLineItem[];
  name: string;
  individualId?: string;
  rank?: string;
  section?: string;
  documentNumber: string;
  transactionId?: string;
  signature?: string;
  preOpenedWindow?: Window | null;
}

export interface GenerateChangeDocumentResult {
  documentEntityId?: string;
}

/**
 * Consolidate line items that share the same LIN + NSN (and for Exchange,
 * the same issue/turn-in direction).  Quantities are summed and total_cost
 * is recalculated from unit_cost × combined qty.
 */
function consolidateLineItems(
  documentType: ChangeDocumentType,
  items: ChangeDocumentLineItem[],
): ChangeDocumentLineItem[] {
  const grouped = new Map<string, ChangeDocumentLineItem>();

  for (const item of items) {
    // Include issue_turn_in in the key for Exchange so Issue and Turn In
    // rows for the same LIN/NSN stay separate.
    const key =
      documentType === 'ChangeDocumentExchange'
        ? `${item.lin}\0${item.nsn}\0${item.issue_turn_in}`
        : `${item.lin}\0${item.nsn}`;

    const existing = grouped.get(key);
    if (!existing) {
      // Clone so we don't mutate the caller's object
      grouped.set(key, { ...item });
      continue;
    }

    // Determine which qty field to sum based on document type
    if (documentType === 'ChangeDocumentIssue') {
      const prev = parseInt(existing.qty_issued || '0', 10) || 0;
      const add = parseInt(item.qty_issued || '0', 10) || 0;
      existing.qty_issued = String(prev + add);
    } else if (documentType === 'ChangeDocumentTurnIn') {
      const prev = parseInt(existing.qty_turn_in || '0', 10) || 0;
      const add = parseInt(item.qty_turn_in || '0', 10) || 0;
      existing.qty_turn_in = String(prev + add);
    } else {
      const prev = parseInt(existing.qty_action || '0', 10) || 0;
      const add = parseInt(item.qty_action || '0', 10) || 0;
      existing.qty_action = String(prev + add);
    }

    // Recalculate total_cost from unit_cost × new qty
    const unitCost = parseFloat((existing.unit_cost || '0').replace(/[$,]/g, '')) || 0;
    if (unitCost) {
      const qtyField =
        documentType === 'ChangeDocumentIssue'
          ? 'qty_issued'
          : documentType === 'ChangeDocumentTurnIn'
            ? 'qty_turn_in'
            : 'qty_action';
      const newQty = parseInt(existing[qtyField] || '0', 10) || 0;
      existing.total_cost = formatCurrency(unitCost * newQty);
    } else {
      // Sum total_cost directly when unit_cost isn't available
      const prevCost = parseFloat((existing.total_cost || '0').replace(/[$,]/g, '')) || 0;
      const addCost = parseFloat((item.total_cost || '0').replace(/[$,]/g, '')) || 0;
      const summed = prevCost + addCost;
      existing.total_cost = summed ? formatCurrency(summed) : '';
    }
  }

  return Array.from(grouped.values());
}

/**
 * Compute totals fields based on the document type and line items.
 */
function computeTotals(
  documentType: ChangeDocumentType,
  items: ChangeDocumentLineItem[],
): Record<string, string> {
  const sumCost = (subset: ChangeDocumentLineItem[]) => {
    const raw = subset.reduce(
      (acc, i) => acc + (parseFloat((i.total_cost || '0').replace(/[$,]/g, '')) || 0),
      0,
    );
    return formatCurrency(raw, true);
  };

  if (documentType === 'ChangeDocumentExchange') {
    const issueItems = items.filter((i) => i.issue_turn_in === 'Issue');
    const turnInItems = items.filter((i) => i.issue_turn_in === 'Turn In');
    const sumQty = (subset: ChangeDocumentLineItem[]) =>
      subset.reduce((acc, i) => acc + (parseInt(i.qty_action || '0', 10) || 0), 0);
    return {
      total_issue_qty: String(sumQty(issueItems)),
      total_issue_cost: sumCost(issueItems),
      total_turn_in_qty: String(sumQty(turnInItems)),
      total_turn_in_cost: sumCost(turnInItems),
    };
  }

  const qtyField = documentType === 'ChangeDocumentIssue' ? 'qty_issued' : 'qty_turn_in';
  const totalQty = items.reduce(
    (acc, i) => acc + (parseInt(i[qtyField] || '0', 10) || 0),
    0,
  );
  return {
    total_qty: String(totalQty),
    total_cost_sum: sumCost(items),
  };
}

/**
 * Generate a Change Document PDF from pre-built line items.
 *
 * Handles SSAN masking, date formatting, calling the document service,
 * opening the PDF in the browser, and cleaning up pre-opened windows on failure.
 *
 * Returns the document entity ID on success, or null if generation failed.
 */
export const generateChangeDocument = async (
  params: GenerateChangeDocumentParams,
): Promise<GenerateChangeDocumentResult | null> => {
  const {
    documentType,
    items,
    name,
    individualId = '',
    rank = '',
    section = '',
    documentNumber,
    transactionId,
    signature,
    preOpenedWindow,
  } = params;

  if (items.length === 0) {
    if (preOpenedWindow && !preOpenedWindow.closed) preOpenedWindow.close();
    return null;
  }

  const now = new Date();
  const dateOfChange = now.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  const ssanLast4 = individualId.length >= 4 ? `XXX-XX-${individualId.slice(-4)}` : '';

  const consolidated = consolidateLineItems(documentType, items);
  const totals = computeTotals(documentType, consolidated);

  try {
    const docResult = await documentService.generateDocument({
      documentType,
      payload: {
        name,
        ssan: ssanLast4,
        rank,
        section,
        date_of_change: dateOfChange,
        document_number: documentNumber,
        transactionInfo: transactionId ? { id: transactionId } : undefined,
        items: consolidated,
        ...totals,
        signature_date: dateOfChange,
        individual_signature: signature || undefined,
      },
      ...(transactionId ? { options: { metadata: { transactionId } } } : {}),
    });

    if (docResult.outputBuffer) {
      const opened = openBase64PdfInWindow(docResult.outputBuffer, preOpenedWindow);
      if (!opened && docResult.downloadUrl) {
        // Inline open failed — fall back to authenticated download
        const fallbackUrl = documentService.resolveDownloadUrl(docResult.downloadUrl);
        if (fallbackUrl) await openDocumentWithAuth(fallbackUrl);
      }
    } else if (docResult.downloadUrl) {
      const url = documentService.resolveDownloadUrl(docResult.downloadUrl);
      if (url) {
        await openDocumentWithAuth(url, preOpenedWindow);
      } else if (preOpenedWindow && !preOpenedWindow.closed) {
        preOpenedWindow.close();
      }
    } else if (preOpenedWindow && !preOpenedWindow.closed) {
      preOpenedWindow.close();
    }

    return { documentEntityId: docResult.documentEntityId ?? undefined };
  } catch (error) {
    log.error('Failed to generate Change Document', { error: String(error) });
    if (preOpenedWindow && !preOpenedWindow.closed) preOpenedWindow.close();
    return null;
  }
};
